import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { buildByDaySlot, normISO } from './calendar/utils';

import { CalendarTopBar } from './calendar/CalendarTopBar';
import { DesktopCalendar } from './calendar/DesktopCalendar';

// ✅ NEW modals
import MDButton from 'components/MDButton';
import { isoToday } from '../data/mockCalendarData';
import { MobileDayList } from './calendar/MobileDayList/MobileDayList';
import { sessionsApi } from '../api/sessionsApi';
import { attendanceApi } from '../../attendance/api/attendanceApi';
import { AttendanceMarkModal } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/AttendanceMarkModal';
import { AttendanceQueueModal } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/modals/AttendanceQueueModal/AttendanceQueueModal';
import { companySettingsApi } from 'core/companySettings';

export default function WeeklyCalendarGrid({
  editMode = false,
  onEnterEdit = () => {},

  attendanceMode = false,
  onEnterAttendance = () => {},

  attendanceListOpen = false,
  setAttendanceListOpen = () => {},

  onOpenAttendanceList = () => {},
  title = 'Seans Takvimi',
  subtitle,
  days = [],
  slots = [],
  trainers = [],
  sessions = [],
  onCopyPickTarget,
  onToday,
  onSessionClick,
  onMobileCopyRequest,

  onEmptyCellClick,
  onPrevWeek,
  onNextWeek,
  onAddSession,
  copyMode = false,
  copyTarget = null,
  onCopyPickSession,
  onCopyCancel,
  onAttendanceSubmit, // ({sessionId,status,note}) => Promise|void
  onOpenWeekCopy,
  // ✅ NEW
  onSessionEdit, // (session) => void
  onSessionDelete, // (session) => void|Promise
  ghostMode = false,
  ghostSessions = [],
  timeColWidth = 64,
  minCellHeight = 64,
  cellPad = 6,
  cellGap = 6,
  filterTrainerId = 'ALL',
  setFilterTrainerId,
  onMoveRequest, // ✅ EKLE
  onMoveSession,
  onReloadSessions, // ✅ EKLE: parent'tan tekrar seans çekmek için
}) {
  const theme = useMuiTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const today = isoToday();
  function isoLocalTRToday() {
    // TR timezone: Europe/Istanbul (kullanıcı timezone)
    // Tarayıcı TR değilse bile doğru davranması için locale bazlı değil,
    // ISO date'e en yakın güvenli yol:
    const now = new Date();
    // local date string -> yyyy-mm-dd
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function isFutureISO(dayISO, todayISO) {
    // YYYY-MM-DD string compare güvenli
    return String(dayISO || '') > String(todayISO || '');
  }
  const todayISO = React.useMemo(() => isoLocalTRToday(), []);
  // ✅ 1) normalize days
  const daysNorm = React.useMemo(() => (days || []).map(normISO), [days]);
  const [queueOpen, setQueueOpen] = useState(false);
  // ✅ 2) trainer map
  const trainerById = React.useMemo(() => {
    const m = new Map();
    (trainers || []).forEach((t) => m.set(String(t.id), t));
    return m;
  }, [trainers]);

  // ✅ 3) UI state

  // ✅ NEW: Attendance Mode

  const [statusFilter, setStatusFilter] = React.useState('ALL');

  const activeSessions = sessions;

  // ✅ 4) weekLabel
  const weekLabel = React.useMemo(() => {
    if (!daysNorm?.length) return 'Bu hafta';
    return `${daysNorm[0]} — ${daysNorm[6]}`;
  }, [daysNorm]);

  // ✅ 5) filtered sessions (ACTIVE listeden filtre)
  const filteredSessions = React.useMemo(() => {
    return (activeSessions || []).filter((s) => {
      if (filterTrainerId !== 'ALL' && String(s.trainerId) !== String(filterTrainerId))
        return false;

      const cancelled = Boolean(s.cancelled);

      if (statusFilter === 'SUCCESS') return s.attended === true && !cancelled;
      if (statusFilter === 'PENDING')
        return (s.attended === null || typeof s.attended === 'undefined') && !cancelled;
      if (statusFilter === 'CANCELLED') return cancelled;

      return true; // ALL
    });
  }, [activeSessions, filterTrainerId, statusFilter]);

  const SETTINGS_KEY = 'solis:companySettings:v1';

  function readCompanySettingsFromCache() {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value ?? null;
    } catch {
      return null;
    }
  }

  // ✅ state
  const [companySettings, setCompanySettings] = React.useState(() =>
    readCompanySettingsFromCache()
  );

  // ✅ dinle (modal save sonrası event ile güncellenecek)
  React.useEffect(() => {
    const onChanged = () => setCompanySettings(readCompanySettingsFromCache());
    window.addEventListener('solis:companySettings:changed', onChanged);
    window.addEventListener('storage', onChanged); // farklı tab için

    return () => {
      window.removeEventListener('solis:companySettings:changed', onChanged);
      window.removeEventListener('storage', onChanged);
    };
  }, []);

  // ✅ toolsProps artık doğru yerden
  const toolsProps = companySettings?.sectorSettings?.tools ?? null;
  const calType = toolsProps?.calendarType || 'TAM_SAAT';
  const isHalfHalf = String(calType) === 'YARIM_SAAT_YARIM';

  // ✅ 30dk grid: satırları daha kompakt göster
  const uiMinCellHeight = isHalfHalf ? 44 : minCellHeight; // (istersen 40-48 arası oynarsın)
  const uiCellPad = isHalfHalf ? Math.max(3, Math.round(cellPad * 0.4)) : cellPad;
  const uiCellGap = isHalfHalf ? Math.max(3, Math.round(cellGap * 0.4)) : cellGap;

  const byDaySlot = React.useMemo(() => {
    const built = buildByDaySlot(filteredSessions, calType);
    return built instanceof Map ? new Map(built) : built;
  }, [filteredSessions, calType]);

  const ghostByDaySlot = React.useMemo(() => {
    if (!ghostMode) return new Map();
    const built = buildByDaySlot(ghostSessions || [], calType);
    return built instanceof Map ? new Map(built) : new Map();
  }, [ghostMode, ghostSessions, calType]);

  // ===========================
  // ✅ Attendance: queue + mark modals
  // ===========================
  const [markOpen, setMarkOpen] = React.useState(false);
  const [pickedSessionId, setPickedSessionId] = React.useState(null);

  const [refreshTick, setRefreshTick] = React.useState(0);
  // "yoklaması girilmemiş" listesi: attended null/undefined ve cancelled değil
  const pendingAttendanceList = React.useMemo(() => {
    return (filteredSessions || []).filter((s) => {
      const cancelled = Boolean(s.cancelled);
      const pending = s.attended === null || typeof s.attended === 'undefined';
      const future = isFutureISO(s?.dayISO || s?.day || s?.date || '', todayISO);
      // ↑ session objende tarih alanı neyse ona göre: dayISO zaten varsa süper.
      // yoksa day / date gibi alternatifleri deniyoruz.

      return pending && !cancelled && !future; // ✅ ileri tarihler yok
    });
  }, [filteredSessions, todayISO]);

  const openMarkFor = (s) => {
    setPickedSessionId(s?.id ?? null);
    setMarkOpen(true);
  };

  const handleAttendanceClick = (session) => {
    const day = session?.dayISO || session?.day || session?.date || '';
    if (isFutureISO(day, todayISO)) return; // ✅ ileri tarih yoklama yok
    openMarkFor(session);
  };

  const handleAttendanceSubmit = async ({ sessionId, status, note }) => {
    // şimdilik: dışarı callback varsa çalıştır, yoksa console
    try {
      if (onAttendanceSubmit) {
        await onAttendanceSubmit({ sessionId, status, note });
      } else {
        // eslint-disable-next-line no-console
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Attendance submit failed:', e);
    }
  };

  // ===========================
  // ✅ Mod toggle rules (çakışmasın)
  // ===========================

  // ===========================
  // ✅ Delete confirm
  // ===========================
  const [delSession, setDelSession] = React.useState(null);
  const [delMeta, setDelMeta] = React.useState({ title: '', message: '' });

  const isPastISO = (dayISO, todayISO) => String(dayISO || '') < String(todayISO || '');

  // “yoklama var” algısı (modeline göre esnek)
  const hasAttendance = (s) => {
    if (s?.attended === true || s?.attended === false) return true;

    const ps = s?.participants;
    if (Array.isArray(ps)) {
      return ps.some((p) => p?.attendanceStatus && p.attendanceStatus !== 'PENDING');
    }
    return false;
  };

  const buildDeleteText = (s) => {
    const day = s?.dayISO || s?.day || s?.date || '';
    const attended = hasAttendance(s);

    if (attended) {
      return {
        title: 'Yoklama Var',
        message: 'Bu seansa yoklama girilmiş. Yine de silmek istiyor musun?',
      };
    }
    if (isFutureISO(day, todayISO)) {
      return {
        title: 'İleri Tarihli Seans',
        message: 'Bu seans ileri tarihli. Silmek istediğine emin misin?',
      };
    }
    if (isPastISO(day, todayISO)) {
      return {
        title: 'Geçmiş Tarihli Seans',
        message:
          'Bu seans geçmiş tarihli. Seans gerçekleşmiş olabilir. Silmek istediğine emin misin?',
      };
    }
    return { title: 'Seansı Sil', message: 'Bu seansı silmek istediğine emin misin?' };
  };

  const [moveMode, setMoveMode] = React.useState(false);
  const [moveFrom, setMoveFrom] = React.useState(null);

  useEffect(() => {
    if (!copyMode) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCopyCancel?.();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [copyMode, onCopyCancel]);
  const sessionsById = React.useMemo(() => {
    const m = new Map();
    (sessions || []).forEach((x) => m.set(String(x.id), x));
    return m;
  }, [sessions]);

  const pickedSession = React.useMemo(() => {
    if (!pickedSessionId) return null;
    return sessionsById.get(String(pickedSessionId)) || null;
  }, [pickedSessionId, sessionsById]);

  return (
    <Card
      sx={{
        position: 'relative',
        borderRadius: '18px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'visible',
        display: { xs: 'block', md: 'flex' },
        flexDirection: { md: 'column' },
        height: { xs: 'auto', md: '100%' },
        minHeight: { xs: 'unset', md: 0 },
        ...(copyMode
          ? {
              backgroundImage:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(245,158,11,0.06), rgba(0,0,0,0))'
                  : 'linear-gradient(180deg, rgba(245,158,11,0.05), rgba(0,0,0,0))',
            }
          : null),
      }}
    >
      {/* ✅ TOPBAR */}
      <CalendarTopBar
        weekLabel={weekLabel}
        toolsProps={toolsProps} // ✅ EKLE
        trainers={trainers}
        filterTrainerId={filterTrainerId}
        setFilterTrainerId={setFilterTrainerId}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        editMode={editMode}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        onAddSession={onAddSession}
        onEnterEdit={onEnterEdit}
        onEnterAttendance={onEnterAttendance}
        onOpenAttendanceList={onOpenAttendanceList || (() => setAttendanceListOpen(true))}
        // ✅ attendance (tek kaynak)
        attendanceMode={attendanceMode}
        // ✅ soft lock (istersen)
        lockEdit={attendanceMode}
        lockAttendance={editMode}
        onOpenWeekCopy={onOpenWeekCopy}
        onToday={onToday}
      />
      {copyMode ? (
        <MDBox
          sx={(theme) => ({
            px: 2,
            py: 1.1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,0))'
                : 'linear-gradient(180deg, rgba(0,0,0,.04), rgba(0,0,0,0))',
          })}
        >
          <MDTypography variant="button" fontWeight="bold">
            Kopyalanacak seansı seç (hedef: {copyTarget?.dayISO} •{' '}
            {String(copyTarget?.startMin ?? '')})
          </MDTypography>

          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => onCopyCancel?.()}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 800, height: 40 }}
          >
            İptal
          </MDButton>
        </MDBox>
      ) : null}

      {subtitle ? (
        <>
          <MDBox sx={{ px: 1.2, py: 0.8 }}>
            <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {subtitle}
            </MDTypography>
          </MDBox>
          <Divider />
        </>
      ) : (
        <Divider />
      )}

      <MDBox
        sx={{
          flex: { md: 1 },
          minHeight: { xs: 'unset', md: 0 },
          position: 'relative', // ✅ EKLE
        }}
      >
        {/* ✅ PREVIEW OVERLAY */}
        {ghostMode ? (
          <MDBox
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '16px',
              background:
                theme.palette.mode === 'dark'
                  ? 'rgba(0,0,0,.18)' // ✅ hafif dim (dark)
                  : 'rgba(15,23,42,.08)', // ✅ hafif dim (light)
              backdropFilter: 'blur(1px)',
              pointerEvents: 'none', // ✅ inceleme/scroll engellenmesin
              zIndex: 4,
            }}
          />
        ) : null}
        {!isDesktop ? (
          <MobileDayList
            days={daysNorm}
            slots={slots}
            today={today}
            trainersById={trainerById}
            byDaySlot={byDaySlot}
            onSessionClick={(s) => {
              if (attendanceMode) return handleAttendanceClick(s);
              onSessionClick?.(s);
            }}
            // ✅ yeni
            editMode={editMode}
            copyMode={copyMode}
            attendanceMode={attendanceMode}
            onSessionEdit={onSessionEdit}
            onSessionDelete={onSessionDelete}
            onMoveRequest={(payload) => {
              if (ghostMode) return;
              return onMoveRequest?.(payload); // ✅ modal açma işi parent’ta
            }}
            onPickCreateSlot={(slot) => {
              // slot = {dayISO, startMin}
              onEmptyCellClick?.(slot); // sende create için emptySlot set ediyordu
            }}
            onCopyStart={(payload) => {
              if (ghostMode) return;
              return onMobileCopyRequest?.(payload);
            }}
          />
        ) : (
          <DesktopCalendar
            days={daysNorm}
            key={`${daysNorm?.[0] || ''}-${refreshTick}`}
            slots={slots}
            today={today}
            trainersById={trainerById}
            isHalfHalf={isHalfHalf}
            byDaySlot={byDaySlot}
            onSessionClick={(s) => {
              if (copyMode) return onCopyPickSession?.(s); // ✅ seans seçimi
              if (attendanceMode) return handleAttendanceClick(s);
              onSessionClick?.(s);
            }}
            onMoveSession={
              ghostMode
                ? undefined
                : async (p) => {
                    // ✅ DEĞİŞTİR
                    await Promise.resolve(onMoveSession?.(p));
                    setRefreshTick((x) => x + 1);
                  }
            }
            // ✅ EKLE
            // ✅ EKLE
            onSessionEdit={
              ghostMode
                ? undefined
                : (s) => (onSessionEdit ? onSessionEdit(s) : onSessionClick?.(s))
            } // ✅ DEĞİŞTİR
            onSessionDelete={ghostMode ? undefined : (s) => onSessionDelete?.(s)} // ✅ DEĞİŞTİR
            timeColWidth={timeColWidth}
            minCellHeight={uiMinCellHeight}
            cellPad={uiCellPad}
            cellGap={uiCellGap}
            editMode={editMode}
            onEmptyClick={ghostMode ? undefined : onEmptyCellClick} // ✅ DEĞİŞTİR
            attendanceMode={attendanceMode}
            onAttendanceClick={handleAttendanceClick}
            filterTrainerId={filterTrainerId}
            // ✅ NEW move-mode props
            moveMode={moveMode}
            setMoveMode={setMoveMode}
            moveFrom={moveFrom}
            setMoveFrom={setMoveFrom}
            sessionsForCheck={sessions} // ✅ filtrelenmemiş ham seans listesi
            copyMode={copyMode}
            onCopyCancel={onCopyCancel} // ✅ EKLE
            ghostByDaySlot={ghostByDaySlot}
            ghostMode={ghostMode}
          />
        )}
        <AttendanceMarkModal
          open={markOpen}
          onClose={() => {
            setMarkOpen(false);
            setPickedSessionId(null);
          }}
          session={pickedSession}
          trainersById={trainerById}
          fetchParticipants={async (sid) => {
            const res = await attendanceApi.getSessionParticipants(sid, 5);
            return res?.participants ?? [];
          }}
          onSave={async (payload) => {
            // sende dışarıdan onAttendanceSubmit var, yoksa direkt api çağır
            if (onAttendanceSubmit) return onAttendanceSubmit(payload);
            // örnek: attendanceApi.mark(pickedSessionId, payload) gibi
          }}
          onWeeklyRefresh={async () => {
            await Promise.resolve(onReloadSessions?.());
          }}
        />
      </MDBox>
    </Card>
  );
}
